import { useEffect } from 'react';
import { createRoute, lazyRouteComponent, Outlet, useNavigate } from '@tanstack/react-router';
import { rootRoute } from './__root';
import { Shell } from '@/components/layout/shell';
import { AppHeader } from '@/components/layout/app-header';
import { AuthGuard } from '@/security/auth-guard';

// Login route — no shell wrapper
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: lazyRouteComponent(
    () => import('@/features/authentication/pages/login-page'),
  ),
});

// App layout route — wraps protected pages with Shell + AppHeader
const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  component: function AppLayout() {
    return (
      <Shell>
        <AppHeader>
          <main className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <Outlet />
          </main>
        </AppHeader>
      </Shell>
    );
  },
});

const dashboardRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/dashboard',
  component: lazyRouteComponent(
    () => import('@/features/dashboard/pages/dashboard-page'),
  ),
});

const documentsIndexRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/documents',
  component: lazyRouteComponent(
    () => import('@/features/documents/pages/document-list-page'),
  ),
});

const documentsDocumentIdRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/documents/$documentId',
  component: lazyRouteComponent(
    () => import('@/features/documents/pages/document-review-page'),
  ),
});

const DropFolderMonitorPageGuarded = lazyRouteComponent(
  () => import('@/features/drop-folder/pages/drop-folder-monitor-page'),
);

const dropFolderMonitorRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/drop-folder',
  component: function DropFolderMonitorRouteGuard() {
    return (
      <AuthGuard requiredRole="admin">
        <DropFolderMonitorPageGuarded />
      </AuthGuard>
    );
  },
});

const reportsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/reports',
  component: lazyRouteComponent(
    () => import('@/features/reports/pages/reports-page'),
  ),
});

const workActivityReportRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/reports/work-activity',
  component: lazyRouteComponent(
    () => import('@/features/reports/pages/work-activity-report-page'),
  ),
});

const ExceptionReportPageGuarded = lazyRouteComponent(
  () => import('@/features/reports/pages/exception-report-page'),
);

const exceptionReportRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/reports/exceptions',
  component: function ExceptionReportRouteGuard() {
    return (
      <AuthGuard requiredRole="admin">
        <ExceptionReportPageGuarded />
      </AuthGuard>
    );
  },
});

const DocumentStatusReportPageGuarded = lazyRouteComponent(
  () => import('@/features/reports/pages/document-status-report-page'),
);

const documentStatusReportRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/reports/document-status',
  component: function DocumentStatusReportRouteGuard() {
    return (
      <AuthGuard requiredRole="admin">
        <DocumentStatusReportPageGuarded />
      </AuthGuard>
    );
  },
});

const ExportPageGuarded = lazyRouteComponent(
  () => import('@/features/export-import/pages/export-page'),
);

const exportRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/export',
  component: function ExportRouteGuard() {
    return (
      <AuthGuard requiredRole="admin">
        <ExportPageGuarded />
      </AuthGuard>
    );
  },
});

const legacyExportImportRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/export-import',
  component: function LegacyExportImportRedirect() {
    const navigate = useNavigate();

    useEffect(() => {
      void navigate({ to: '/export', replace: true });
    }, [navigate]);

    return null;
  },
});

const templatesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/templates',
  component: lazyRouteComponent(
    () => import('@/features/templates/pages/template-list-page'),
  ),
});

const templateCreateRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/templates/new',
  component: lazyRouteComponent(
    () => import('@/features/templates/pages/template-source-upload-page'),
  ),
});

const templateViewRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/templates/$templateId/view',
  component: lazyRouteComponent(
    () => import('@/features/templates/pages/template-form-page'),
  ),
});

const templateEditRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/templates/$templateId/edit',
  component: lazyRouteComponent(
    () => import('@/features/templates/pages/template-form-page'),
  ),
});

const templateCopyRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/templates/$templateId/copy',
  component: lazyRouteComponent(
    () => import('@/features/templates/pages/template-form-page'),
  ),
});

const CourtFormRegistryPageGuarded = lazyRouteComponent(
  () => import('@/features/recognition/pages/court-form-registry-page'),
);

const courtFormRegistryRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/court-forms',
  component: function CourtFormRegistryRouteGuard() {
    return (
      <AuthGuard requiredRole="admin">
        <CourtFormRegistryPageGuarded />
      </AuthGuard>
    );
  },
});

const entitiesIndexRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/entities',
  component: lazyRouteComponent(
    () => import('@/features/entities/pages/entity-list-page'),
  ),
});

const entityDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/entities/$entityId',
  component: lazyRouteComponent(
    () => import('@/features/entities/pages/entity-detail-page'),
  ),
});

const accountsIndexRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/accounts',
  component: lazyRouteComponent(
    () => import('@/features/entities/pages/account-list-page'),
  ),
});

const accountDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/accounts/$accountId',
  component: lazyRouteComponent(
    () => import('@/features/entities/pages/account-detail-page'),
  ),
});

// Admin-only routes — wrapped with AuthGuard
const SorImportPageGuarded = lazyRouteComponent(
  () => import('@/features/sor/pages/sor-import-page'),
);

const sorImportRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/sor',
  component: function SorImportRouteGuard() {
    return (
      <AuthGuard requiredRole="admin">
        <SorImportPageGuarded />
      </AuthGuard>
    );
  },
});

const UsersPageGuarded = lazyRouteComponent(
  () => import('@/features/users/pages/users-page'),
);

const usersRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/users',
  component: function UsersRouteGuard() {
    return (
      <AuthGuard requiredRole="admin">
        <UsersPageGuarded />
      </AuthGuard>
    );
  },
});

const AuditPageGuarded = lazyRouteComponent(
  () => import('@/features/audit/pages/audit-log-page'),
);

const auditRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/audit-logs',
  component: function AuditRouteGuard() {
    return (
      <AuthGuard requiredRole="admin">
        <AuditPageGuarded />
      </AuthGuard>
    );
  },
});

const BillingPageGuarded = lazyRouteComponent(
  () => import('@/features/billing/pages/billing-page'),
);

const billingRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/billing',
  component: function BillingRouteGuard() {
    return (
      <AuthGuard requiredRole="admin">
        <BillingPageGuarded />
      </AuthGuard>
    );
  },
});

const FileNamingPageGuarded = lazyRouteComponent(() => import('@/features/settings/pages/file-naming-page'));
const fileNamingRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings/file-naming',
  component: function FileNamingRouteGuard() {
    return <AuthGuard requiredRole="admin"><FileNamingPageGuarded /></AuthGuard>;
  },
});

export const routeTree = rootRoute.addChildren([
  loginRoute,
  appLayoutRoute.addChildren([
    dashboardRoute,
    documentsIndexRoute,
    documentsDocumentIdRoute,
    dropFolderMonitorRoute,
    reportsRoute,
    workActivityReportRoute,
    exceptionReportRoute,
    documentStatusReportRoute,
    exportRoute,
    legacyExportImportRoute,
    templatesRoute,
    templateCreateRoute,
    templateViewRoute,
    templateEditRoute,
    templateCopyRoute,
    courtFormRegistryRoute,
    entitiesIndexRoute,
    entityDetailRoute,
    accountsIndexRoute,
    accountDetailRoute,
    sorImportRoute,
    usersRoute,
    auditRoute,
    billingRoute,
    fileNamingRoute,
  ]),
]);
